import type { Defined } from '@dresscode-frontend/utility-types';
import { useInfiniteQuery } from '@tanstack/react-query';

import type { ApiSearchQuery } from '@/lib/api/api-type-helper';
import { apiClient } from '@/lib/api/client';
import { getDataOrThrowApiError } from '@/lib/api/error';
import { getPath } from '@/lib/api/path';

export const useWorkerTypes = (
  type: 'EMPLOYEE' | 'BOARD_MEMBER' | 'CONTRACTOR' | 'COLLABORATOR',
  searchText: string = '',
) => {
  const result = useInfiniteQuery({
    queryKey: [
      getPath('/v2/worker-types-for-select'),
      { type, searchText },
    ] as const,
    queryFn: async ({ queryKey: [path, { type, searchText }], pageParam }) => {
      type Query = ApiSearchQuery<'/v2/worker-types-for-select', 'get'>;
      return await apiClient
        .GET(path, {
          params: {
            query: {
              kind: type as Defined<Query>['kind'],
              freeText: searchText,
              page: pageParam as number,
            },
          },
        })
        .then((res) => getDataOrThrowApiError(res));
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.page < lastPage.maxPage ? lastPage.page + 1 : undefined;
    },
  });

  return {
    workerTypes:
      result.data?.pages.flatMap((page) =>
        page.items.map((workerType) => ({
          id: workerType.id,
          value: workerType.id,
          label: workerType.name,
        })),
      ) || [],
    isFetchingNextPage: result.isFetchingNextPage,
    isLoading: result.isLoading,
    error: result.error,
    fetchNextPage: result.fetchNextPage,
  };
};
